/**
 * Display formatters for `<TeamsTranscriptPicker>` rows.
 *
 * Split out of the component file so they are directly unit-testable and so
 * the component module exports components only (react-refresh boundary).
 *
 * Both functions exist because Microsoft Graph data is routinely incomplete
 * under app-only auth — missing names and missing subjects are the NORMAL
 * case, not an edge case — and a picker that renders blanks or "Invalid Date"
 * reads as broken.
 */
import type { TeamsParticipant } from "@/hooks/queries/interview-analysis/useTeamsTranscripts";

/**
 * Human date for a meeting, tolerant of a missing or unparseable timestamp.
 *
 * Never renders "Invalid Date" — a transcript we cannot date is still listed
 * and still importable, so the fallback has to be a phrase a recruiter can
 * read.
 */
export function formatMeetingDate(iso: string | null): string {
  if (!iso) return "Date unavailable";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Date unavailable";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Participant summary that degrades cleanly as names disappear.
 *
 * `identity.user.displayName` is null far more often than not under app-only
 * Graph auth, so the nameless path is the primary path. Named people are
 * listed; everyone else folds into a count ("Priya Anand +2 others") rather
 * than rendering an empty slot. With no names at all it degrades to a bare
 * count, and with nothing at all it returns `""` so the caller omits the
 * separator instead of printing a dangling "·".
 */
export function formatParticipants(
  participants: TeamsParticipant[],
  total: number,
): string {
  const named = participants
    .map((p) => p.display_name)
    .filter((n): n is string => typeof n === "string" && n.trim().length > 0)
    .map((n) => n.trim());

  if (named.length === 0) {
    if (total <= 0) return "";
    return total === 1 ? "1 participant" : `${total} participants`;
  }

  const shown = named.slice(0, 2);
  const remainder = total - shown.length;
  if (remainder <= 0) return shown.join(", ");
  return `${shown.join(", ")} +${remainder} other${remainder === 1 ? "" : "s"}`;
}
